import type { QueryKey } from '@tanstack/react-query';

export interface StaticApiQueryAnnotationsData<TQueryKey extends QueryKey = QueryKey> {
  queryKey: TQueryKey;
}

export type DynamicApiQueryAnnotationsData<
  TArgs extends any[],
  TQueryKey extends QueryKey = QueryKey,
> = (...args: TArgs) => StaticApiQueryAnnotationsData<TQueryKey>;

export type ApiQueryAnnotationsData<
  TArgs extends any[] = any,
  TQueryKey extends QueryKey = QueryKey,
> = StaticApiQueryAnnotationsData<TQueryKey> | DynamicApiQueryAnnotationsData<TArgs, TQueryKey>;

// ToDo: This constant should be of type unique symbol to avoid collisions with other values,
// but due to a bug in TS this leads to an error.
export const API_QUERY_ANNOTATIONS = 'API_QUERY_ANNOTATIONS';

export type ApiQueryAnnotations<
  TArgs extends any[] = any[],
  TQueryKey extends QueryKey = QueryKey,
> = {
  [API_QUERY_ANNOTATIONS]: ApiQueryAnnotationsData<TArgs, TQueryKey>;
};

export type ApiQueryMethod<TArgs extends any[], TResult> = (...args: TArgs) => Promise<TResult>;

export type AnnotatedApiQueryMethod<
  TArgs extends any[],
  TResult,
  TQueryKey extends QueryKey = QueryKey,
> = ApiQueryMethod<TArgs, TResult> & ApiQueryAnnotations<TArgs, TQueryKey>;

export const makeApiQuery = <TArgs extends any[], TResult, TQueryKey extends QueryKey = QueryKey>(
  method: ApiQueryMethod<TArgs, TResult>,
  annotations?: ApiQueryAnnotationsData<TArgs, TQueryKey>,
): AnnotatedApiQueryMethod<
  TArgs,
  TResult,
  TQueryKey | readonly [...TQueryKey, TArgs] | readonly [TArgs]
> => {
  const result = (...args: TArgs) => method(...args);

  const annotationsFactory =
    typeof annotations === 'function'
      ? annotations
      : (...args: TArgs) => ({
          queryKey: annotations?.queryKey
            ? ([...annotations.queryKey, args] as const)
            : ([args] as const),
        });

  return Object.assign(result, {
    [API_QUERY_ANNOTATIONS]: annotationsFactory,
  });
};

// ToDo: This constant should be of type unique symbol to avoid collisions with other values,
// but due to a bug in TS this leads to an error.
export const API_MUTATION_ANNOTATIONS = 'API_MUTATION_ANNOTATIONS';

export type ApiMutationMethodFactory<TParams extends any[], TVariables, TResult> = (
  ...params: TParams
) => (variables: TVariables) => Promise<TResult>;

interface ApiMutationAnnotationsData<TParams extends any[]> {
  affectedQueries?: Array<AnnotatedApiQueryMethod<TParams, any, any>>;
}

export type ApiMutationAnnotations<TParams extends any[]> = {
  [API_MUTATION_ANNOTATIONS]: ApiMutationAnnotationsData<TParams>;
};

export type AnnotatedApiMutationMethodFactory<
  TParams extends any[],
  TVariables,
  TResult,
> = ApiMutationMethodFactory<TParams, TVariables, TResult> & ApiMutationAnnotations<TParams>;

export const makeApiMutation = <TParams extends any[], TVariables = void, TResult = void>(
  method: ApiMutationMethodFactory<TParams, TVariables, TResult>,
  affectedQueries?: Array<AnnotatedApiQueryMethod<TParams, any, any>>,
): AnnotatedApiMutationMethodFactory<TParams, TVariables, TResult> => {
  const result = (...params: TParams) => method(...params);

  return Object.assign(result, {
    [API_MUTATION_ANNOTATIONS]: {
      affectedQueries,
    },
  });
};
